/**
 * Offside — Law 11. (§5.4)
 *
 * "Get this exactly right, it's the most-argued rule."
 *
 * Two halves, split across two moments in the tick:
 *
 *   1. POSITION, at the instant the ball is played. `evaluateOffsidePositions`
 *      is the source of truth for who is in an offside POSITION. It is called by
 *      ball.ts's resolveKicks at kick time (step.ts stage 6) to fill the frozen
 *      OffsideSnapshot — the decision is made against that snapshot, never
 *      against live positions, because one tick of lag is ~14cm at sprint speed
 *      and that is the margin most calls turn on (DECISIONS.md D8).
 *
 *   2. OFFENCE, later. Being in an offside position is not an offence in itself.
 *      `evaluateOffsideInterference` (step.ts stage 9) reads the snapshot and
 *      blows up only when a flagged player becomes involved in active play by
 *      touching the ball — the modern application: flag, but don't whistle until
 *      they interfere.
 *
 * This module READS the snapshot for the offence decision and WRITES it only via
 * `evaluateOffsidePositions`, which ball.ts owns the call site of. The rules
 * evaluator never writes the snapshot.
 *
 * A player is in an offside POSITION iff, at the moment the ball is played:
 *   - they are in the opponent's half (strictly past the halfway line), AND
 *   - they are nearer the opponent's goal line than BOTH the ball AND the
 *     second-last opponent.
 * Level with either the ball or the second-last opponent is ONSIDE (strict >).
 * On or behind the halfway line is ONSIDE.
 */

import type { OffsideSnapshot, StepResult, WorldState } from '../types.js';
import { MatchPhase, MAX_PLAYERS } from '../types.js';
import { STOPPAGE_FOUL_S } from '../constants.js';
import { addStoppage, attackDir, beginRestart } from './fsm.js';

/**
 * Fill `out` with the offside POSITION of every player of `attackingTeam` at the
 * current tick. Call this at kick time (from ball.ts's resolveKicks) with the
 * snapshot the rules module later reads.
 *
 * Projections are taken along the attacking axis so the comparison is a single
 * signed number regardless of which way the team is shooting. In POD player
 * state the "forward-most body point" is the capsule centre — arms/hands are not
 * modelled, so the head/body/feet rule (§5.4) is exactly the centre here.
 */
export function evaluateOffsidePositions(
  state: WorldState,
  attackingTeam: number,
  passer: number,
  fromRestart: MatchPhase,
  out: OffsideSnapshot,
): void {
  const p = state.players;
  out.offsidePosition.fill(0);
  out.tick = state.tick;
  out.team = attackingTeam;
  out.passer = passer;
  out.fromRestart = fromRestart;

  const dir = attackDir(state, attackingTeam);
  const half = state.config.pitchLength * 0.5;
  const defendingTeam = 1 - attackingTeam;
  const ballProj = dir * state.ball.px;

  // Two deepest opponents by attack-axis projection. The deepest is usually the
  // GK (last); the SECOND-deepest is the offside line (second-last opponent).
  let firstProj = Number.NEGATIVE_INFINITY;
  let secondProj = Number.NEGATIVE_INFINITY;
  let firstPx = -dir * half;
  let secondPx = -dir * half;
  let defenders = 0;
  for (let i = 0; i < MAX_PLAYERS; i++) {
    if (p.active[i] !== 1 || p.sentOff[i] === 1) continue;
    if (p.team[i] !== defendingTeam) continue;
    defenders++;
    const proj = dir * p.px[i]!;
    if (proj >= firstProj) {
      secondProj = firstProj;
      secondPx = firstPx;
      firstProj = proj;
      firstPx = p.px[i]!;
    } else if (proj > secondProj) {
      secondProj = proj;
      secondPx = p.px[i]!;
    }
  }

  // With fewer than two opponents no offside line can be drawn — nobody offside.
  if (defenders < 2) {
    out.lineCoord = dir * half;
    return;
  }
  out.lineCoord = secondPx;

  const defLineProj = secondProj;
  for (let i = 0; i < MAX_PLAYERS; i++) {
    if (p.active[i] !== 1 || p.sentOff[i] === 1) continue;
    if (p.team[i] !== attackingTeam) continue;
    const proj = dir * p.px[i]!;
    // Opponent's half AND ahead of both the ball and the second-last opponent.
    if (proj > 0 && proj > ballProj && proj > defLineProj) {
      out.offsidePosition[i] = 1;
    }
  }
}

/** Restarts a receiver can be onside directly from — throw-in/goal-kick/corner. (§5.4) */
function isExemptRestart(fromRestart: MatchPhase): boolean {
  return (
    fromRestart === MatchPhase.ThrowIn ||
    fromRestart === MatchPhase.GoalKick ||
    fromRestart === MatchPhase.Corner
  );
}

/**
 * Detect an offside OFFENCE from the snapshot: a flagged attacker has since
 * touched the ball (interfered with play). Awards an indirect free kick to the
 * defending team and carries the offside line for the VAR-style overlay (§5.4).
 */
export function evaluateOffsideInterference(state: WorldState, out: StepResult): void {
  if (!state.config.offsideEnabled) return;
  if (state.match.phase !== MatchPhase.InPlay) return;

  const snap = state.offside;
  if (snap.tick < 0) return; // no live snapshot
  if (isExemptRestart(snap.fromRestart)) return;

  const b = state.ball;
  const toucher = b.lastTouchedBy;
  if (toucher < 0) return;
  if (b.lastTouchTick <= snap.tick) return; // no new touch since the ball was played
  if (b.lastTouchedTeam !== snap.team) return; // an opponent played it → wiped clean
  if (toucher === snap.passer) return; // the passer cannot be offside from their own pass
  if (snap.offsidePosition[toucher] !== 1) return; // receiver was onside

  const defendingTeam = 1 - snap.team;
  const x = b.px;
  const z = b.pz;
  out.events.push({
    type: 'OFFSIDE',
    tick: state.tick,
    team: defendingTeam,
    player: toucher,
    x,
    z,
    offsideLine: snap.lineCoord,
  });
  addStoppage(state, STOPPAGE_FOUL_S);
  // Indirect free kick to the defending team where the offside player interfered.
  beginRestart(state, MatchPhase.FreeKick, defendingTeam, x, z);
}
